import { useEffect, useMemo, useState } from "react";
import { Printer, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label as UILabel } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LabelSheet, LABEL_SIZES } from "./LabelSheet";
import { useAuth } from "../../context/AuthContext";
import { useHardware } from "../../context/HardwareContext";

const OPT_KEY = "pos_label_opts";

export const LabelPrintDialog = ({ products, open, onOpenChange }) => {
  const { store } = useAuth();
  const { printLabels, state } = useHardware();
  const [qty, setQty] = useState({});
  const [opts, setOpts] = useState(() => ({ showName: true, showPrice: true, showStore: false, size: "receipt", ...JSON.parse(localStorage.getItem(OPT_KEY) || "{}") }));

  useEffect(() => {
    if (open) setQty(Object.fromEntries(products.map((p) => [p.id, 1])));
  }, [open, products]);

  const setOpt = (patch) => {
    const n = { ...opts, ...patch };
    setOpts(n);
    localStorage.setItem(OPT_KEY, JSON.stringify(n));
  };

  const labels = useMemo(() => products.flatMap((p) => Array.from({ length: qty[p.id] || 0 }, () => ({ name: p.name, barcode: p.barcode, price: p.price }))), [products, qty]);
  const missing = products.filter((p) => !p.barcode).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="label-print-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Tag className="h-5 w-5" />Cetak Label Barcode</DialogTitle>
          <DialogDescription>{products.length} produk dipilih · {labels.length} label akan dicetak</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2 max-h-60 overflow-y-auto thin-scroll pr-1">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-lg border px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{p.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{p.barcode || "— belum ada barcode —"}</div>
                  </div>
                  <Input className="w-16 h-8 text-center font-mono" inputMode="numeric" value={qty[p.id] ?? 0}
                    onChange={(e) => setQty({ ...qty, [p.id]: Math.min(200, Number(e.target.value.replace(/\D/g, "")) || 0) })} data-testid={`label-qty-${p.id}`} />
                </div>
              ))}
            </div>
            {missing > 0 && <p className="text-xs text-warning" data-testid="label-missing-barcode">{missing} produk belum memiliki barcode (label tetap dicetak tanpa barcode).</p>}
            <div className="space-y-1.5">
              <UILabel className="text-xs">Ukuran label (untuk cetak via browser)</UILabel>
              <Select value={opts.size} onValueChange={(v) => setOpt({ size: v })}>
                <SelectTrigger data-testid="label-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LABEL_SIZES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[["showName", "Tampilkan nama produk"], ["showPrice", "Tampilkan harga"], ["showStore", "Tampilkan nama toko"]].map(([k, l]) => (
              <label key={k} className="flex items-center justify-between text-sm">
                <span>{l}</span>
                <Switch checked={opts[k]} onCheckedChange={(v) => setOpt({ [k]: v })} data-testid={`label-opt-${k}`} />
              </label>
            ))}
            <p className="text-xs text-muted-foreground">
              {state.printer ? `Dicetak langsung ke ${state.printer.name} (ESC/POS, barcode CODE128).` : "Printer belum terhubung — label dicetak lewat dialog print browser."}
            </p>
          </div>
          <div className="rounded-xl bg-muted/60 p-4 max-h-[420px] overflow-y-auto thin-scroll" data-testid="label-preview">
            <div className="bg-white rounded p-2 mx-auto w-fit min-w-[200px]">
              <LabelSheet labels={labels.slice(0, 12)} store={store} opts={opts} />
            </div>
            {labels.length > 12 && <p className="text-xs text-center text-muted-foreground mt-2">+{labels.length - 12} label lainnya</p>}
          </div>
        </div>
        <Button className="w-full h-11" disabled={!labels.length} onClick={() => printLabels(labels, opts)} data-testid="label-print-btn">
          <Printer className="h-4 w-4 mr-2" />Cetak {labels.length} Label
        </Button>
      </DialogContent>
    </Dialog>
  );
};
